import { useParams } from 'react-router-dom'
import { FileText, GitBranch, Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CharterStatusBadge } from './CharterStatusBadge'
import { CharterExportButton } from './CharterExportButton'
import type { RatifiedCharter, CharterStatus } from '../../redux/types/charterTypes'

interface CharterViewerProps {
  charter: RatifiedCharter
  status: CharterStatus | string | null
  version: number
  teamId: number
  isStudent?: boolean
  /** "Propose Amendment" click handler. The host (CharterCardModal) swaps its
   *  own dialog content to the amendment form — avoids a Radix Dialog-in-Dialog. */
  onProposeAmendment?: () => void
}

const SECTION_ORDER: [keyof RatifiedCharter, string][] = [
  ['project_overview', 'Project Overview'],
  ['individual_goals_and_strengths', 'Individual Goals & Strengths'],
  ['communication_plan', 'Communication Plan'],
  ['deliverables_and_deadlines', 'Deliverables & Deadlines'],
  ['roles_and_responsibilities', 'Roles & Responsibilities'],
  ['conflict_resolution', 'Conflict Resolution'],
  ['team_boundaries', 'Team Boundaries'],
  ['operations', 'Operations'],
  ['leadership_structure', 'Leadership Structure'],
  ['resources_and_support', 'Resources & Support'],
]

export function CharterViewer({
  charter,
  status,
  version,
  teamId,
  isStudent = false,
  onProposeAmendment,
}: CharterViewerProps) {
  const { roomId = '' } = useParams<{ roomId: string }>()

  const isLocked = status === 'locked'
  const isRenegotiating = status === 'renegotiating'

  return (
    <div className='w-full space-y-6'>
      {/* Header */}
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-1 min-w-0'>
          <div className='flex items-center gap-3 flex-wrap min-w-0'>
            <Shield className='h-6 w-6 text-indigo-600' />
            <h1 className='text-xl font-bold text-gray-900'>Team Charter</h1>
            <Badge variant='outline' className='text-indigo-700 border-indigo-200 bg-indigo-50'>
              Version {version}
            </Badge>
            <CharterStatusBadge status={status} />
          </div>
          {isRenegotiating && (
            <p className='text-sm text-amber-600'>
              A re-negotiation is currently in progress. Some sections may be updated.
            </p>
          )}
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          <CharterExportButton roomId={roomId} teamId={teamId} version={version} />
        </div>
      </div>

      {/* Sections */}
      {SECTION_ORDER.map(([key, title]) => {
        const value = charter[key]
        if (!value) return null

        return (
          <Card key={key} className='border-gray-200'>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base font-semibold text-gray-900 flex items-center gap-2'>
                <FileText className='h-4 w-4 text-indigo-500' />
                {title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Separator className='mb-4' />
              {key === 'deliverables_and_deadlines' && Array.isArray(value) ? (
                <DeliverablesTable deliverables={value} />
              ) : key === 'individual_goals_and_strengths' &&
                typeof value === 'object' &&
                !Array.isArray(value) ? (
                <GoalsStrengthsView data={value as Record<string, string>} />
              ) : (
                <p className='text-sm text-gray-700 whitespace-pre-line leading-relaxed'>
                  {String(value)}
                </p>
              )}
            </CardContent>
          </Card>
        )
      })}

      {/* Re-negotiation CTA */}
      {isStudent && isLocked && !isRenegotiating && (
        <div className='flex justify-center pt-4'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => onProposeAmendment?.()}
            className='gap-1.5 text-amber-600 border-amber-300 hover:bg-amber-50'
          >
            <GitBranch className='h-4 w-4' />
            Propose Amendment
          </Button>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Sub-renderers
// ──────────────────────────────────────────────────────────

interface Deliverable {
  description: string
  owner: string
  deadline: string
  dependencies: string[]
}

function DeliverablesTable({ deliverables }: { deliverables: Deliverable[] }) {
  if (!deliverables.length) return <p className='text-sm text-gray-400 italic'>None specified.</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Deliverable</TableHead>
          <TableHead>Owner</TableHead>
          <TableHead>Deadline</TableHead>
          <TableHead>Dependencies</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {deliverables.map((d, i) => (
          <TableRow key={i}>
            <TableCell className='text-sm font-medium'>{d.description}</TableCell>
            <TableCell className='text-sm'>{d.owner}</TableCell>
            <TableCell className='text-sm'>{d.deadline}</TableCell>
            <TableCell className='text-sm text-gray-500'>
              {d.dependencies?.join(', ') || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function GoalsStrengthsView({ data }: { data: Record<string, string> }) {
  return (
    <div className='space-y-3'>
      {Object.entries(data).map(([name, text]) => (
        <div key={name}>
          <p className='text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-0.5'>
            {name}
          </p>
          <p className='text-sm text-gray-700'>{text}</p>
        </div>
      ))}
    </div>
  )
}
